import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../runtime-config';
import authService from '../services';

// To change auth provider, edit src/services/index.js

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const checkAuthState = useCallback(async () => {
    try {
      const authState = await authService.checkAuthState();
      setAuthError(authState.error || "");
      if (authState.authenticated) {
        console.log('User authenticated:', authState.user.email);
        setUser(authState.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.log('Auth check error:', error.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    // Initialize the auth service
    await authService.initialize(config);
    // Check if user is already authenticated
    await checkAuthState();
  }, [checkAuthState]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const signUpUser = async (email, password) => {
    const result = await authService.signUp(email, password);
    return result;
  };

  const confirmSignUpUser = async (email, code) => {
    const result = await authService.confirmSignUp(email, code);
    return result;
  };

  const signInUser = async (email, password) => {
    const result = await authService.signIn(email, password);
    
    if (result.success && !result.requiresNewPassword) {
      // Wait a moment then refresh auth state
      setTimeout(async () => {
        await checkAuthState();
      }, 500);
    }
    
    return result;
  };

  const completeNewPasswordUser = async (newPassword) => {
    const result = await authService.completeNewPassword(newPassword);
    if (result.success) {
      await checkAuthState();
    }
    return result;
  };

  const signOutUser = async () => {
    const result = await authService.signOut();
    if (result.success) {
      setUser(null);
    }
    return result;
  };

  const getIdToken = useCallback(async () => {
    try {
      return await authService.getIdToken();
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, []);

  const resetPasswordUser = async (email) => {
    const result = await authService.resetPassword(email);
    return result;
  };

  const confirmResetPasswordUser = async (email, confirmationCode, newPassword) => {
    const result = await authService.confirmResetPassword(email, confirmationCode, newPassword);
    return result;
  };

  const value = {
    user,
    loading,
    authError,
    endavaSsoAvailable: Boolean(config.aws.endavaSso || config.aws.userPoolId === "us-east-1_n5Tp5dPdU"),
    signUp: signUpUser,
    confirmSignUp: confirmSignUpUser,
    signIn: signInUser,
    signInWithEndava: () => authService.signInWithEndava(),
    completeNewPassword: completeNewPasswordUser,
    signOut: signOutUser,
    resetPassword: resetPasswordUser,
    confirmResetPassword: confirmResetPasswordUser,
    getIdToken,
    // Legacy methods for compatibility
    login: signInUser,
    logout: signOutUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
